import React, { memo, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@common/components/button';
import Alert from '@common/components/alert';
import { TextInput } from '@common/components/form/inputs/text/text-input';
import { Checkbox } from '@common/components/form/inputs/checkbox/checkbox-input';
import { test as validationTest } from '@utils/validation';
import { isWorkEmail } from './domain';
import { TrialArrow } from './trial-arrow';
import type { DetailsErrorKind } from './details-step';

type AccountStep = 'details' | 'verify' | 'password';

export type VerifyErrorKind = 'invalid' | 'rate_limited' | 'failed';

type Props = {
  initialEmail: string;
  error: DetailsErrorKind | null;
  verifyError: VerifyErrorKind | null;
  isStarting: boolean;
  isVerifying: boolean;
  isResending: boolean;
  isSubmitting: boolean;
  onStart: (values: { firstName: string; lastName: string; email: string }) => Promise<boolean>;
  onVerify: (code: string) => Promise<boolean>;
  onResend: () => Promise<boolean>;
  onComplete: (password: string) => Promise<boolean>;
};

const CODE_SLOTS = ['a', 'b', 'c', 'd', 'e', 'f'] as const;
const TITLE_ID = 'trial-account-title';

/** Each manager reads its own attribute. The password step does not use these. */
const ignorePasswordManager = {
  'data-1p-ignore': true,
  'data-lpignore': 'true',
  'data-bwignore': true,
  'data-protonpass-ignore': 'true',
  'data-form-type': 'other',
} as const;

export const AccountModal = memo(({
  initialEmail,
  error,
  verifyError,
  isStarting,
  isVerifying,
  isResending,
  isSubmitting,
  onStart,
  onVerify,
  onResend,
  onComplete,
}: Props) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<AccountStep>('details');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [consent, setConsent] = useState(false);

  const emailValid = isWorkEmail(email);
  const passwordValid = password.length >= 8 && validationTest('PASSWORD', password);
  const codeComplete = code.every((digit) => digit.length === 1);
  const canStart = !!firstName.trim() && !!lastName.trim() && emailValid && consent;
  const onVerifyRef = useRef(onVerify);
  const lastAutoSubmitted = useRef('');
  const verifyRequest = useRef(false);
  const codeWasComplete = useRef(false);

  onVerifyRef.current = onVerify;

  useEffect(() => {
    if (step !== 'verify') return;

    document.getElementById('trial-code-0')?.focus();
  }, [step]);

  useEffect(() => {
    if (step !== 'verify') return;

    if (!codeComplete) {
      lastAutoSubmitted.current = '';
      codeWasComplete.current = false;
      return;
    }

    const becameComplete = !codeWasComplete.current;
    codeWasComplete.current = true;

    if (!becameComplete) return;

    const value = code.join('');

    if (lastAutoSubmitted.current === value || verifyRequest.current) return;

    lastAutoSubmitted.current = value;
    verifyRequest.current = true;
    onVerifyRef.current(value).then((verified) => {
      if (verified) setStep('password');
    }).finally(() => {
      verifyRequest.current = false;
    });
  }, [code, codeComplete, step]);
  const passwordAlertKey = error === 'unverified'
    ? 'authentication:trial_password_error_unverified'
    : error === 'invalid_password'
      ? 'authentication:trial_password_error_invalid'
      : `authentication:trial_details_error_${error}`;

  const focusCode = (index: number) => {
    const input = document.getElementById(`trial-code-${index}`) as HTMLInputElement | null;

    input?.focus();
    input?.select();
  };

  const pasteCode = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 6).split('');

    if (!digits.length) return;

    setCode((current) => current.map((item, slot) => digits[slot] ?? item));
    focusCode(Math.min(digits.length, 5));
  };

  const setDigit = (index: number, value: string) => {
    const digits = value.replace(/\D/g, '');

    if (digits.length > 1) {
      pasteCode(digits);
      return;
    }

    const digit = digits;

    setCode((current) => current.map((item, i) => (i === index ? digit : item)));

    if (digit && index < 5) focusCode(index + 1);
  };

  const removeDigit = (index: number) => {
    if (code[index]) {
      setCode((current) => current.map((item, i) => (i === index ? '' : item)));
      return;
    }

    if (index === 0) return;

    setCode((current) => current.map((item, i) => (i === index - 1 ? '' : item)));
    focusCode(index - 1);
  };

  return (
    <div
      className={
        'FreeTrial__ModalScrim max-md:!tw-fixed max-md:!tw-items-end max-md:!tw-justify-stretch '
        + 'max-md:!tw-overflow-hidden max-md:!tw-p-0'
      }
    >
      <section
        className={
          'FreeTrial__Modal max-md:!tw-w-full max-md:!tw-max-h-[min(92vh,760px)] '
          + 'max-md:!tw-overflow-y-auto max-md:!tw-rounded-b-none max-md:!tw-rounded-t-[28px]'
        }
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
      >
        <div className="FreeTrial__ModalDots" aria-hidden="true">
          <span className={step === 'details' ? 'is-current' : 'is-done'} />
          <span className={step === 'verify' ? 'is-current' : step === 'password' ? 'is-done' : undefined} />
          <span className={step === 'password' ? 'is-current' : undefined} />
        </div>

        {step === 'details' && (
          <form
            {...ignorePasswordManager}
            onSubmit={async (e) => {
              e.preventDefault();
              if (!canStart || isStarting) return;

              const started = await onStart({
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                email: email.trim(),
              });

              if (started) setStep('verify');
            }}
          >
            <h1 id={TITLE_ID}>{t('authentication:trial_account_title')}</h1>
            {error === 'existing' && (
              <Alert type="warning">
                <Trans i18nKey="authentication:trial_details_error_existing" components={[<a href="/auth/login" />]} />
              </Alert>
            )}
            {error && error !== 'existing' && (
              <Alert type="warning">{t(`authentication:trial_details_error_${error}`)}</Alert>
            )}
            <div className="FreeTrial__FieldGrid">
              <div className="FreeTrial__Field">
                <label htmlFor="trial-first-name">{t('authentication:trial_details_first_name')}</label>
                <TextInput
                  id="trial-first-name"
                  name="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  autoComplete="given-name"
                  autoFocus
                  {...ignorePasswordManager}
                />
              </div>
              <div className="FreeTrial__Field">
                <label htmlFor="trial-last-name">{t('authentication:trial_account_last_name')}</label>
                <TextInput
                  id="trial-last-name"
                  name="family-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  autoComplete="family-name"
                  {...ignorePasswordManager}
                />
              </div>
            </div>
            <div className="FreeTrial__Field">
              <label htmlFor="trial-email">{t('authentication:trial_details_email')}</label>
              <TextInput
                id="trial-email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                error={email.length > 0 && !emailValid}
                {...ignorePasswordManager}
              />
              <span className={email.length > 0 && !emailValid ? 'is-invalid' : undefined}>
                {t('authentication:trial_details_email_hint')}
              </span>
            </div>
            <Checkbox
              value={consent}
              onChange={setConsent}
              label={(
                <Trans
                  i18nKey="authentication:trial_account_privacy"
                  components={[<a href={t('authentication:consent_url')} target="_blank" rel="noreferrer" />]}
                />
              )}
            />
            <Button
              type="primary"
              size="fill"
              buttonType="submit"
              disabled={!canStart}
              isLoading={isStarting}
            >
              <span className="FreeTrial__Cta">
                {t('authentication:trial_preview_continue')}
                <TrialArrow />
              </span>
            </Button>
          </form>
        )}

        {step === 'verify' && (
          <form
            {...ignorePasswordManager}
            onSubmit={async (e) => {
              e.preventDefault();
              if (!codeComplete || isVerifying || verifyRequest.current) return;

              const value = code.join('');

              lastAutoSubmitted.current = value;
              verifyRequest.current = true;

              try {
                const verified = await onVerify(value);

                if (verified) setStep('password');
              } finally {
                verifyRequest.current = false;
              }
            }}
          >
            <h1 id={TITLE_ID}>{t('authentication:trial_verify_title')}</h1>
            <p>
              <Trans
                i18nKey="authentication:trial_verify_description"
                values={{ email }}
                components={[<b />]}
              />
            </p>
            {verifyError && <Alert type="warning">{t(`authentication:trial_verify_error_${verifyError}`)}</Alert>}
            <div className="FreeTrial__Code">
              {CODE_SLOTS.map((slot, index) => (
                <input
                  key={slot}
                  id={`trial-code-${index}`}
                  value={code[index]}
                  inputMode="numeric"
                  maxLength={1}
                  aria-label={t('authentication:trial_verify_digit', { current: index + 1, total: 6 })}
                  autoComplete={index === 0 ? 'one-time-code' : 'off'}
                  {...ignorePasswordManager}
                  onChange={(e) => setDigit(index, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Backspace' && e.key !== 'Delete') return;

                    e.preventDefault();
                    removeDigit(index);
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    pasteCode(e.clipboardData.getData('text'));
                  }}
                />
              ))}
            </div>
            <Button type="primary" size="fill" buttonType="submit" disabled={!codeComplete} isLoading={isVerifying}>
              {t('authentication:trial_verify_submit')}
            </Button>
            <p className="FreeTrial__Resend">
              <Trans
                i18nKey="authentication:trial_verify_resend"
                components={[<button
                  type="button"
                  disabled={isResending}
                  onClick={async () => {
                    if (isResending) return;

                    const sent = await onResend();

                    if (!sent) return;

                    setCode(['', '', '', '', '', '']);
                    document.getElementById('trial-code-0')?.focus();
                  }}
                />]}
              />
            </p>
          </form>
        )}

        {step === 'password' && (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!passwordValid || isSubmitting) return;

              await onComplete(password);
            }}
          >
            <div className="FreeTrial__ModalTop">
              <span className="FreeTrial__Verified">{t('authentication:trial_password_verified')}</span>
            </div>
            <h1 id={TITLE_ID}>{t('authentication:trial_password_title')}</h1>
            {error && <Alert type="warning">{t(passwordAlertKey)}</Alert>}
            <div className="FreeTrial__Field">
              <label htmlFor="trial-password">{t('authentication:trial_details_password')}</label>
              <div className="FreeTrial__Password">
                <TextInput
                  id="trial-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  autoFocus
                  error={password.length > 0 && !passwordValid}
                />
                <button type="button" onClick={() => setShowPassword((current) => !current)}>
                  {t(showPassword ? 'authentication:trial_password_hide' : 'authentication:trial_password_show')}
                </button>
              </div>
              <span className={password.length > 0 && !passwordValid ? 'is-invalid' : undefined}>
                {t(password.length > 0 && !passwordValid
                  ? 'authentication:trial_password_error_invalid'
                  : 'authentication:trial_password_hint')}
              </span>
            </div>
            <Button type="primary" size="fill" buttonType="submit" disabled={!passwordValid} isLoading={isSubmitting}>
              <span className="FreeTrial__Cta">
                {t('authentication:trial_password_complete')}
                <TrialArrow />
              </span>
            </Button>
          </form>
        )}
      </section>
    </div>
  );
});
